// refsys/src/components/header/header.jsx
import React, { useImperativeHandle, forwardRef, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Modal } from 'bootstrap';

const Header = forwardRef((props, ref) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('login');
  const [modal, setModal] = useState(null);
  const [userBusinessLogo, setUserBusinessLogo] = useState(null);
  const [businessColors, setBusinessColors] = useState({
    primary_color: '#0d6efd',
    secondary_color: '#6c757d',
    primary_highlight_color: '#0b5ed7',
    secondary_highlight_color: '#5c636a'
  });
   const [userbranch_id, setUserbranch_id] = useState(null);
  
  const [loginFormData, setLoginFormData] = useState({
    email: '',
    password: ''
  });

  const [registerFormData, setRegisterFormData] = useState({
    name: '',
    email: '',
    password: '',
    contactNumber: '',
    businessName: '',
    RegisterFrom: 'Refmex'
  });

  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Fetch business colors when logged in
  const fetchBusinessColors = async (businessId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_RECEIPT_BACK_END_URL}/business/${businessId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setBusinessColors({
          primary_color: data.primary_color || '#0d6efd',
          secondary_color: data.secondary_color || '#6c757d',
          primary_highlight_color: data.primary_highlight_color || '#0b5ed7',
          secondary_highlight_color: data.secondary_highlight_color || '#5c636a'
        });
        // Apply theme colors to document
        applyThemeColors(data);
      }
    } catch (error) {
      console.error('Error fetching business colors:', error);
    }
  };

  // Apply theme colors to CSS variables
  const applyThemeColors = (colors) => {
    const root = document.documentElement;
    if (colors.primary_color) root.style.setProperty('--business-primary', colors.primary_color);
    if (colors.secondary_color) root.style.setProperty('--business-secondary', colors.secondary_color);
    if (colors.primary_highlight_color) root.style.setProperty('--business-primary-highlight', colors.primary_highlight_color);
    if (colors.secondary_highlight_color) root.style.setProperty('--business-secondary-highlight', colors.secondary_highlight_color);
  };

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const user = sessionStorage.getItem("userData");

    if (token && user) {
      setIsLoggedIn(true);
      try {
        const userData = JSON.parse(user);
        if (userData.businesslogo) {
          setUserBusinessLogo(userData.businesslogo);
        }
        if (userData.businessId) {
          fetchBusinessColors(userData.businessId);
        }
        if (userData.branch_id) {
          setUserbranch_id(userData.branch_id);
        }
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    } else {
      setIsLoggedIn(false);
      setUserbranch_id(null);
      setUserBusinessLogo(null);
    }
  }, [location]);

  useEffect(() => {
    const modalElement = document.getElementById('LoginForm');
    if (modalElement) {
      const bsModal = new Modal(modalElement, {
        backdrop: true,
        keyboard: true
      });
      setModal(bsModal);
    }
  }, []);

  useImperativeHandle(ref, () => ({
    showLoginModal: () => {
      setActiveTab('login');
      openModal();
    },
    showRegisterModal: () => {
      setActiveTab('register');
      openModal();
    }
  }));

  const isActive = (path) => {
    return location.pathname === path ? 'active' : '';
  }

  const openModal = () => {
    if (modal) {
      modal.show();
    }
  };

  const closeModal = () => {
    if (modal) {
      modal.hide();
    }
    resetForms();
  };

  const resetForms = () => {
    setLoginFormData({
      email: '',
      password: ''
    });
    setRegisterFormData({
      name: '',
      email: '',
      password: '',
      contactNumber: '',
      businessName: '',
      RegisterFrom: 'Refmex'
    });
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setRegisterFormData((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const handleLoginInputChange = (e) => {
    const { id, value } = e.target;
    setLoginFormData((prevState) => ({
      ...prevState,
      [id === "loginEmail" ? "email" : "password"]: value,
    }));
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_RECEIPT_BACK_END_URL}/referral/login_user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: loginFormData.email,
          password: loginFormData.password
        })
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('Error response:', text);
        await Swal.fire({
          position: "center",
          icon: "error",
          title: "Invalid email or password",
          showConfirmButton: true
        });
        return;
      }
      const data = await response.json();
      sessionStorage.setItem('token', data.token);
      
      const userData = {
        id: data.user.id,
        email: data.user.email,
        name: data.user.name,
        businessId: data.user.businessId,
        contact: data.user.contact,
        branch_id: data.user.branch_id,
        businesslogo: data.business?.logo || null
      };
      sessionStorage.setItem('userData', JSON.stringify(userData));
      
      if (data.business?.logo) {
        setUserBusinessLogo(data.business.logo);
      }
      if (data.user.branch_id) {
        setUserbranch_id(data.user.branch_id);
      }
      // Fetch business colors after login
      if (data.user.businessId) {
        await fetchBusinessColors(data.user.businessId);
      }
      
      closeModal();
      Swal.fire({
        position: "center",
        icon: "success",
        title: "Login Success",
        showConfirmButton: false,
        timer: 1000
      });
      navigate('/sales');
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleLogout = () => {
    sessionStorage.clear(); 
    setIsLoggedIn(false);
    setUserBusinessLogo(null);
    navigate("/");
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const handleRegisterSubmit = async (e) => {
    if (e) e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_RECEIPT_BACK_END_URL}/referral/add_user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(registerFormData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        Swal.fire({
          position: "center",
          icon: "error",
          title: errorData.msg?.charAt(0).toUpperCase() + errorData.msg?.slice(1) || 'Registration failed',
          showConfirmButton: true
        });
        throw new Error('Registration failed');
      }
      
      Swal.fire({
        position: "center",
        icon: "success",
        title: "Registration Successful! Please login.",
        showConfirmButton: true
      });
      
      setActiveTab('login');
      
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <>
      <nav className="navbar navbar-expand-lg navbar-light bg-light sticky-top">
        <div className="container">
          <a className='navbar-brand' href="/"> 
            {location.pathname === '/sales' && userBusinessLogo && isLoggedIn ? (
              <img 
                src={process.env.REACT_APP_RECEIPT_FRONT_END + '/' + userBusinessLogo} 
                alt="Business Logo" 
                style={{ height: '40px' }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/images/logo.png";
                }}
              />
            ) : (
              <img 
                src="/images/logo.png" 
                alt="Logo" 
                style={{ height: '40px' }} 
              />
            )}
          </a>
          <button className="navbar-toggler" type="button" data-toggle="collapse" data-target="#navbarSupportedContent" aria-controls="navbarSupportedContent" aria-expanded="false" aria-label="Toggle navigation">
            <i className="fa fa-bars" aria-hidden="true"></i>
          </button>
          <div className="collapse navbar-collapse menu-main" id="navbarSupportedContent">
            <ul className="navbar-nav ml-auto menu-item">
              <li className="nav-item">
                <a className={`nav-link ${isActive('/')}`} href="/">Home</a>
              </li>
              <li className="nav-item">
                <a className={`nav-link ${isActive('/about-us')}`} href="/about-us">About Us</a>
              </li>
              <li className="nav-item">
                <a className={`nav-link ${isActive('/pricing')}`} href="/pricing">Pricing</a>
              </li>
              <li className="nav-item">
                <a className={`nav-link ${isActive('/developer-docs')}`} href="/developer-docs">Developer Docs</a>
              </li>
              {isLoggedIn && (
                <li className="nav-item">
                  <a className={`nav-link ${isActive('/sales')}`} href="/sales">Dashboard</a>
                </li>
              )}
              <li className="nav-item">
                {isLoggedIn ? (
                  <a className="nav-link bor" href="#" onClick={handleLogout}>
                    Logout
                  </a>
                ) : (
                  <a id="loginLink" className="nav-link bor" href="#" onClick={(e) => {
                    e.preventDefault();
                    setActiveTab('login');
                    openModal();
                  }}>
                    Login
                  </a>
                )}
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Login/Register Modal */}
      <div className="modal fade" id="LoginForm" tabIndex="-1" aria-labelledby="LoginFormLabel" aria-hidden="true">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="LoginFormLabel">{activeTab === 'login' ? 'Login' : 'Register'}</h5>
              <button type="button" className="close" data-dismiss="modal" aria-label="Close" onClick={closeModal}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="tab-content mt-3">
                <div className={`tab-pane fade ${activeTab === 'login' ? 'show active' : ''}`} id="login" role="tabpanel">
                  <form id='login_form' onSubmit={handleLoginSubmit}>
                    <div className="input-group custom-form-input">
                      <div className="input-group-prepend">
                        <span className="input-group-text faq-icon">
                          <i aria-hidden="true">
                            <img width="25" height="25" src="https://img.icons8.com/ios-glyphs/25/0553d9/user--v1.png" alt="user" />
                          </i>
                        </span>
                      </div>
                      <input
                        type="text"
                        className="form-control box-bg contact-input"
                        id="loginEmail"
                        placeholder="Enter Username Or Email"
                        value={loginFormData.email}
                        onChange={handleLoginInputChange}
                      />
                    </div>
                    <div className="input-group custom-form-input">
                      <div className="input-group-prepend">
                        <span className="input-group-text faq-icon">
                          <i aria-hidden="true">
                            <img width="25" height="25" src="https://img.icons8.com/ios-glyphs/25/0553d9/lock.png" alt="lock" />
                          </i>
                        </span>
                      </div>
                      <input
                        type="password"
                        id="loginPassword"
                        className="form-control box-bg contact-input"
                        placeholder="Enter your password"
                        value={loginFormData.password}
                        onChange={handleLoginInputChange}
                      />
                    </div>
                  </form>
                </div>

                <div className={`tab-pane fade ${activeTab === 'register' ? 'show active' : ''}`} id="register" role="tabpanel">
                  <form id='signup_form' onSubmit={handleRegisterSubmit}>
                    <div className="input-group custom-form-input mb-3">
                      <div className="input-group-prepend">
                        <span className="input-group-text faq-icon">
                          <i aria-hidden="true">
                            <img width="25" height="25" src="https://img.icons8.com/ios-glyphs/25/0553d9/user--v1.png" alt="user" />
                          </i>
                        </span>
                      </div>
                      <input
                        type="text"
                        id="name"
                        className="form-control box-bg contact-input"
                        placeholder="Enter your name"
                        value={registerFormData.name}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="input-group custom-form-input mb-3">
                      <div className="input-group-prepend">
                        <span className="input-group-text faq-icon">
                          <i aria-hidden="true">
                            <img width="25" height="25" src="https://img.icons8.com/ios-glyphs/25/0553d9/email.png" alt="email" />
                          </i>
                        </span>
                      </div>
                      <input
                        type="email"
                        id="email"
                        className="form-control box-bg contact-input"
                        placeholder="Enter your email"
                        value={registerFormData.email}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="input-group custom-form-input mb-3">
                      <div className="input-group-prepend">
                        <span className="input-group-text faq-icon">
                          <i aria-hidden="true">
                            <img width="25" height="25" src="https://img.icons8.com/ios-glyphs/25/0553d9/lock.png" alt="lock" />
                          </i>
                        </span>
                      </div>
                      <input
                        type="password"
                        id="password"
                        className="form-control box-bg contact-input"
                        placeholder="Enter your password"
                        value={registerFormData.password}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="input-group custom-form-input mb-3">
                      <div className="input-group-prepend">
                        <span className="input-group-text faq-icon">
                          <i aria-hidden="true">
                            <img width="25" height="25" src="https://img.icons8.com/ios-glyphs/25/0553d9/phone.png" alt="phone" />
                          </i>
                        </span>
                      </div>
                      <input
                        type="number"
                        id="contactNumber"
                        className="form-control box-bg contact-input"
                        placeholder="Enter your contact number"
                        value={registerFormData.contactNumber}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="input-group custom-form-input mb-3">
                      <div className="input-group-prepend">
                        <span className="input-group-text faq-icon">
                          <i aria-hidden="true">
                            <img width="25" height="25" src="https://img.icons8.com/ios-glyphs/25/0553d9/business.png" alt="business" />
                          </i>
                        </span>
                      </div>
                      <input
                        type="text"
                        id="businessName"
                        className="form-control box-bg contact-input"
                        placeholder="Enter your business name"
                        value={registerFormData.businessName}
                        onChange={handleInputChange}
                      />
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div className="modal-footer d-flex justify-content-between">
              <div>
                {activeTab === 'login' ? (
                  <p style={{ color: '#666', fontSize: 'small' }}>
                    Don't have an account?{' '}
                    <span onClick={() => setActiveTab('register')} style={{ cursor: 'pointer', color: businessColors.primary_color }}>
                      Register here
                    </span>
                  </p>
                ) : (
                  <p style={{ color: '#666', fontSize: 'small' }}>
                    Already have an account?{' '}
                    <span onClick={() => setActiveTab('login')} style={{ cursor: 'pointer', color: businessColors.primary_color }}>
                      Login here
                    </span>
                  </p>
                )}
              </div>
              <div className='d-flex'>
                <button type="button" className="btn btn-secondary" style={{ marginRight: '10px' }} onClick={closeModal}>
                  Close
                </button>
                {activeTab === 'login' ? (
                  <button type="submit" form="login_form" className="btn" style={{ backgroundColor: businessColors.primary_color, borderColor: businessColors.primary_color, color: '#fff' }}>
                    Login
                  </button>
                ) : (
                  <button type="submit" form="signup_form" className="btn" style={{ backgroundColor: businessColors.primary_color, borderColor: businessColors.primary_color, color: '#fff' }}>
                    Register
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

export default Header;